import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { apiService } from '@services/api';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';
import { VOVER_LANDING_URL } from '@/constants';

type Mode = 'public' | 'authenticated';
type Step = 'credentials' | 'code' | 'phrase' | 'done';

const POST_DELETION_REDIRECT_MS = 2000;
const PUBLIC_STATUS_404_REDIRECT_MS = 1200;
const STATUS_POLL_MS = 1200;

function normalizeCode(raw: string) {
  return raw.replace(/\D/g, '').slice(0, 6);
}

function normalizePhrase(raw: string) {
  return raw.trim().toLowerCase();
}

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
export default function AccountDeletionFlowModal(props: { open: boolean; onClose: () => void; mode: Mode }) {
  const { open, onClose, mode } = props;

  const [step, setStep] = useState<Step>('credentials');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [progressPct, setProgressPct] = useState<number | null>(null);
  const [progressMsg, setProgressMsg] = useState<string>('Aguardando processamento…');
  const [completed, setCompleted] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [phrase, setPhrase] = useState('');

  /** Evita ticks concorrentes após conclusão / redirecionamento */
  const pollTerminalRef = useRef(false);

  const normalizedCode = useMemo(() => normalizeCode(code), [code]);
  const phraseOk = useMemo(() => normalizePhrase(phrase) === 'excluir', [phrase]);

  const reset = useCallback(() => {
    setStep('credentials');
    setBusy(false);
    setError(null);
    setSuccess(null);
    setRequestId(null);
    setProgressPct(null);
    setProgressMsg('Aguardando processamento…');
    setCompleted(false);
    setFailed(null);
    setEmail('');
    setPassword('');
    setCode('');
    setPhrase('');
    pollTerminalRef.current = false;
  }, []);

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const close = () => {
    if (step !== 'done') {
      reset();
    }
    onClose();
  };

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } =
        mode === 'authenticated'
          ? await apiService.requestAccountDeletion()
          : await apiService.requestAccountDeletionPublic(email.trim(), password);
      if (!data?.success) throw new Error(data?.message || 'Não foi possível enviar o código.');
      setSuccess(data?.message || 'Código enviado por e-mail.');
      setStep('code');
    } catch (e: unknown) {
      setError(getFriendlyErrorMessage(e, 'Não foi possível enviar o código.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } =
        mode === 'authenticated'
          ? await apiService.confirmAccountDeletionCode(normalizedCode)
          : await apiService.confirmAccountDeletionCodePublic(email.trim(), normalizedCode);
      if (!data?.success) throw new Error(data?.message || 'Código inválido ou expirado.');
      setSuccess('Código confirmado. Para continuar, digite EXCLUIR.');
      setStep('phrase');
    } catch (e: unknown) {
      setError(getFriendlyErrorMessage(e, 'Código inválido ou expirado.'));
    } finally {
      setBusy(false);
    }
  };

  const startDeletion = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } =
        mode === 'authenticated'
          ? await apiService.startAccountDeletion('excluir')
          : await apiService.startAccountDeletionPublic(email.trim(), 'excluir');
      if (!data?.success) throw new Error(data?.message || 'Não foi possível iniciar a exclusão.');
      const rid = String(data?.data?.requestId || '');
      setRequestId(rid || null);
      setSuccess('Exclusão iniciada. Acompanhe o progresso abaixo.');
      setStep('done');
    } catch (e: unknown) {
      setError(getFriendlyErrorMessage(e, 'Não foi possível iniciar a exclusão.'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (step !== 'done') return;
    if (mode !== 'authenticated' && (!email.trim() || !requestId)) return;
    if (mode === 'authenticated' && !requestId) return;
    if (completed || failed) return;

    let stopped = false;
    const tick = async () => {
      if (pollTerminalRef.current) return;
      try {
        const { data } =
          mode === 'authenticated'
            ? await apiService.getAccountDeletionStatus()
            : await apiService.getAccountDeletionStatusPublic(email.trim(), requestId as string);
        const doc = data?.data;
        const status = doc?.status as string | undefined;
        const progress = doc?.progress as { percent?: number; message?: string; step?: string } | null | undefined;

        if (progress?.message) setProgressMsg(progress.message);
        if (typeof progress?.percent === 'number') setProgressPct(progress.percent);
        else {
          // fallback: sem percent → mostrar indeterminado enquanto aguarda
          setProgressPct(null);
          if (status === 'scheduled') setProgressMsg('Na fila para processamento…');
          if (status === 'processing') setProgressMsg('Processando exclusão…');
        }

        if (status === 'completed') {
          pollTerminalRef.current = true;
          setCompleted(true);
          setProgressPct(100);
          setProgressMsg('Concluído. Redirecionando…');
          window.setTimeout(() => {
            onClose();
            window.location.assign(VOVER_LANDING_URL);
          }, POST_DELETION_REDIRECT_MS);
        } else if (status === 'failed') {
          const jobErr = doc?.error?.message;
          setFailed(
            typeof jobErr === 'string' && jobErr.trim()
              ? getFriendlyErrorMessage({ message: jobErr }, 'Falha ao concluir a exclusão.')
              : 'Falha ao concluir a exclusão.'
          );
        }
      } catch (e: unknown) {
        const httpStatus = (e as { response?: { status?: number } })?.response?.status;
        if (mode === 'public' && httpStatus === 404) {
          pollTerminalRef.current = true;
          setFailed(null);
          setSuccess(null);
          setCompleted(true);
          setProgressPct(100);
          setProgressMsg('Processo encerrado. Redirecionando…');
          window.setTimeout(() => {
            onClose();
            window.location.assign(VOVER_LANDING_URL);
          }, PUBLIC_STATUS_404_REDIRECT_MS);
          return;
        }
        setFailed(getFriendlyErrorMessage(e, 'Não foi possível consultar o status da exclusão.'));
      }
    };

    tick();
    const id = window.setInterval(() => {
      if (stopped) return;
      void tick();
    }, STATUS_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [open, step, email, requestId, completed, failed, mode, onClose]);

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Excluir conta</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          Este processo é <strong>irreversível</strong>. Ao iniciar a exclusão, os dados operacionais da organização serão removidos.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {failed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {failed}
          </Alert>
        )}

        {step === 'credentials' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {mode === 'authenticated'
                ? 'Para continuar, solicite o código de 6 dígitos por e-mail.'
                : 'Informe o e-mail e a senha do criador da conta para continuar.'}
            </Typography>
            {mode !== 'authenticated' ? (
              <>
                <TextField
                  label="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  autoComplete="email"
                />
                <TextField
                  label="Senha"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  autoComplete="current-password"
                />
              </>
            ) : null}
          </>
        )}

        {step === 'code' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Digite o código de 6 dígitos enviado por e-mail.
            </Typography>
            {mode !== 'authenticated' ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                E-mail: <strong>{email.trim() || '—'}</strong>
              </Typography>
            ) : null}
            <TextField
              label="Código de 6 dígitos"
              value={normalizedCode}
              onChange={(e) => setCode(e.target.value)}
              inputProps={{ inputMode: 'numeric' }}
              fullWidth
            />
          </>
        )}

        {step === 'phrase' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Para iniciar a exclusão, digite <strong>EXCLUIR</strong>.
            </Typography>
            <TextField
              label='Digite "EXCLUIR"'
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              fullWidth
            />
          </>
        )}

        {step === 'done' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {progressMsg}
            </Typography>
            {typeof progressPct === 'number' ? (
              <LinearProgress variant="determinate" value={progressPct} />
            ) : (
              <LinearProgress variant="indeterminate" />
            )}
            {typeof progressPct === 'number' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {progressPct}%
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={close} disabled={busy}>
          Fechar
        </Button>

        {step === 'credentials' && (
          <Button
            variant="contained"
            color="error"
            onClick={requestCode}
            disabled={busy || (mode !== 'authenticated' && (!email.trim() || !password.trim()))}
          >
            Continuar
          </Button>
        )}

        {step === 'code' && (
          <>
            <Button variant="text" onClick={requestCode} disabled={busy}>
              Reenviar código
            </Button>
            <Button variant="contained" onClick={confirmCode} disabled={busy || normalizedCode.length !== 6}>
              Continuar
            </Button>
          </>
        )}

        {step === 'phrase' && (
          <Button variant="contained" color="error" onClick={startDeletion} disabled={busy || !phraseOk}>
            Iniciar exclusão
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

